import React, { useEffect } from "react";
import { FiEye } from "react-icons/fi";
import dayjs from "newui/utils/dayjs";
import { SkeletonBase } from "newui/components/Skeleton/Skeleton";
import { EvaluationAccessBoundary } from "newui/features/Rfx/Evaluation/components/EvaluationAccessBoundary";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { useRfxNavigate } from "app/useRfxNavigate";
import { useRfxReadOnlyPreview } from "./hooks/useRfxReadOnlyPreview";
import { getRelationshipBannerCopy } from "./utils/rfx-relationship-copy";
import type { RfxReadOnlyPreviewResource } from "./types/rfx-read-only-preview.type";
import type { Rfx } from "./types/rfx.types";

export interface RfxReadOnlyPreviewProps {
	/** RFx UUID — this surface is addressed by uuid only, never numeric id. */
	rfxUuid: string;
}

/**
 * Net-new, bidder-style, read-only RFx preview (PRO-637 Task 011). Consumes
 * `GET /api/v2/rfq/{uuid}/preview` (Task 004) and renders published content
 * ONLY — no procurer tabs/actions are reachable from here. Every access
 * decision comes from the server's `access_flags`; this component never
 * re-derives identity/auth from the current user (server-authoritative flags,
 * not heuristics — see `2026-06-30-server-authoritative-flags-not-heuristics`).
 *
 * TASK-029 item 8 (VT-025): an owner/same-company procurer who hard-navigates
 * here directly (bypassing the Panel-row click that normally gates this
 * route to panel members) gets `access_flags.can_view_procurer: true` on the
 * SAME response — this is the existing server-authoritative "is this user
 * the owner/same-company procurer" flag (`RfxAccessFlags`, already consumed
 * read-only elsewhere in this feature), not a new client heuristic. Redirect
 * them to the canonical procurer detail route instead of rendering the
 * cross-company banner/read-only content, which is factually wrong for them
 * and would otherwise strip an owner to read-only on their own RFx.
 * Genuine cross-company panel members/approvers/guests (`can_view_procurer:
 * false`) are unaffected — this redirect is additive.
 */
export const RfxReadOnlyPreview = ({ rfxUuid }: RfxReadOnlyPreviewProps) => {
	const { data, isLoading, error } = useRfxReadOnlyPreview(rfxUuid);
	const { clientPaths } = useNavigationPaths();
	const rfxNavigate = useRfxNavigate();

	const shouldRedirectToProcurerDetail = !!data?.access_flags.can_view_procurer;

	useEffect(() => {
		if (!shouldRedirectToProcurerDetail || !data || !clientPaths) {
			return;
		}

		rfxNavigate(
			clientPaths.viewRfx({ id: data.id, name: data.name } as Rfx),
			{ replace: true }
		);
	}, [shouldRedirectToProcurerDetail, data, clientPaths, rfxNavigate]);

	if (isLoading || shouldRedirectToProcurerDetail) {
		return (
			<div className="space-y-4 p-6" data-testid="rfx-read-only-preview-loading">
				<SkeletonBase width="100%" height="32px" />
				<SkeletonBase width="100%" height="200px" />
			</div>
		);
	}

	return (
		<EvaluationAccessBoundary error={error}>
			{data && <RfxReadOnlyPreviewContent preview={data} />}
		</EvaluationAccessBoundary>
	);
};

interface RfxReadOnlyPreviewContentProps {
	preview: RfxReadOnlyPreviewResource;
}

const RfxReadOnlyPreviewContent = ({
	preview,
}: RfxReadOnlyPreviewContentProps) => {
	const bannerCopy = getRelationshipBannerCopy(preview.access_flags.relationship);

	return (
		<div className="p-6" data-testid="rfx-read-only-preview">
			<div
				className="mb-6 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800"
				data-testid="rfx-read-only-preview-banner"
			>
				<FiEye aria-hidden="true" className="h-5 w-5 flex-shrink-0" />
				<span>{bannerCopy}</span>
			</div>

			<h1 className="text-2xl font-semibold text-gray-900">{preview.name}</h1>
			<p className="mt-1 text-sm text-gray-500">{preview.rfx_id_number}</p>

			<dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
				<div>
					<dt className="text-xs font-medium uppercase text-gray-500">Client</dt>
					<dd className="text-sm text-gray-900">{preview.client}</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase text-gray-500">Location</dt>
					<dd className="text-sm text-gray-900">{preview.location}</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase text-gray-500">
						Submission Deadline
					</dt>
					<dd className="text-sm text-gray-900">
						{preview.submission_deadline
							? `${dayjs(preview.submission_deadline).format("dddd, D MMMM YYYY hh:mm A")} ${preview.submission_timezone ?? ""}`.trim()
							: "—"}
					</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase text-gray-500">Quote Type</dt>
					<dd className="text-sm text-gray-900">{preview.quote_type}</dd>
				</div>
			</dl>

			{preview.description && (
				<div className="mt-6" data-testid="rfx-read-only-preview-description">
					<h2 className="text-sm font-medium uppercase text-gray-500">
						Description
					</h2>
					<p className="mt-2 whitespace-pre-wrap text-sm text-gray-900">
						{preview.description}
					</p>
				</div>
			)}

			<div className="mt-8" data-testid="rfx-read-only-preview-documents">
				<h2 className="text-sm font-medium uppercase text-gray-500">Documents</h2>
				{preview.documents.length === 0 ? (
					<p className="mt-2 text-sm text-gray-500">No documents published.</p>
				) : (
					<ul className="mt-2 divide-y divide-gray-100">
						{preview.documents.map((document) => (
							<li
								key={document.uuid}
								className="flex items-center justify-between py-2"
								data-testid={`rfx-read-only-preview-document-${document.uuid}`}
							>
								<span className="text-sm text-gray-900">
									{document.file_name}
								</span>
								{document.download_url && (
									<a
										href={document.download_url}
										target="_blank"
										rel="noreferrer"
										className="text-sm font-medium text-blue-600 hover:text-blue-700"
										data-testid={`rfx-read-only-preview-document-download-${document.uuid}`}
									>
										Download
									</a>
								)}
							</li>
						))}
					</ul>
				)}
			</div>

			<div className="mt-8" data-testid="rfx-read-only-preview-addenda">
				<h2 className="text-sm font-medium uppercase text-gray-500">Addenda</h2>
				{preview.addenda.length === 0 ? (
					<p className="mt-2 text-sm text-gray-500">No addenda published.</p>
				) : (
					<ul className="mt-2 space-y-4">
						{preview.addenda.map((addendum) => (
							<li
								key={addendum.uuid}
								data-testid={`rfx-read-only-preview-addendum-${addendum.uuid}`}
							>
								<p className="text-sm text-gray-900">{addendum.addendum}</p>
								{addendum.answers.map((answer) => (
									<p
										key={answer.uuid}
										className="mt-1 pl-4 text-sm text-gray-600"
									>
										{answer.addendum}
									</p>
								))}
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	);
};
